import { useMemo } from "react";
import { useChild } from "../../../contexts/ChildContext";
import { useCase } from "../../../contexts/CaseContext";
import { useParent } from "../../../contexts/ParentContext";
import { Child, Parent, ProgramType, NoShowStats, NO_SHOW_PARTY_LABELS } from "@visitproof/types";
import {
  Race,
  Sex,
  PrimaryLanguage,
  ReasonForSupervision,
  AgeGroup,
  MaritalStatus,
  IncomeLevel,
  EducationLevel,
  EmploymentStatus,
  HousingStatus,
  PresentingConcern,
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  SPECIAL_NEEDS_LABELS,
  REASON_LABELS,
  AGE_GROUP_LABELS,
  MARITAL_STATUS_LABELS,
  INCOME_LEVEL_LABELS,
  EDUCATION_LEVEL_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  HOUSING_STATUS_LABELS,
  PRESENTING_CONCERN_LABELS,
  getAgeGroupFromDob,
} from "@visitproof/types";


export interface DemographicCount {
  key: string;
  label: string;
  count: number;
  percentage: number;
}

export interface DemographicsData {
  // Totals
  totalChildren: number;
  totalParents: number;
  totalCases: number;

  // Child demographics
  childRace: DemographicCount[];
  childSex: DemographicCount[];
  childLanguage: DemographicCount[];
  childAgeGroups: DemographicCount[];
  specialNeeds: DemographicCount[];

  // Parent demographics
  parentRace: DemographicCount[];
  parentSex: DemographicCount[];
  parentLanguage: DemographicCount[];
  parentMaritalStatus: DemographicCount[];
  parentIncomeLevel: DemographicCount[];
  parentEducationLevel: DemographicCount[];
  parentEmploymentStatus: DemographicCount[];
  parentHousingStatus: DemographicCount[];
  parentRuralArea: DemographicCount[];
  parentTranslatorNeeded: DemographicCount[];

  // Case demographics
  reasonForSupervision: DemographicCount[];
  presentingConcerns: DemographicCount[];
  noShowsByParty: DemographicCount[];
  totalNoShows: number;

  // Coverage stats
  childrenWithDemographics: number;
  parentsWithDemographics: number;
  casesWithDemographics: number;
}

const countByField = <T extends string>(
  items: { [key: string]: T | undefined }[],
  field: string,
  labels: Record<T, string>
): DemographicCount[] => {
  const counts: Record<string, number> = {};
  let notSetCount = 0;
  const total = items.length;

  items.forEach((item) => {
    const value = item[field] as T | undefined;
    if (value && value in labels) {
      counts[value] = (counts[value] || 0) + 1;
    } else {
      notSetCount++;
    }
  });

  const result: DemographicCount[] = (Object.entries(labels) as [string, string][]).map(([key, label]) => ({
    key,
    label,
    count: counts[key] || 0,
    percentage: total > 0 ? Math.round(((counts[key] || 0) / total) * 100) : 0,
  }));

  // Add "Not Set" category so users can see data coverage gaps
  if (notSetCount > 0) {
    result.push({
      key: "_not_set",
      label: "Not Set",
      count: notSetCount,
      percentage: total > 0 ? Math.round((notSetCount / total) * 100) : 0,
    });
  }

  // Sort by count descending
  return result.sort((a, b) => b.count - a.count);
};

const countSpecialNeeds = (children: Child[]): DemographicCount[] => {
  const counts: Record<string, number> = {};
  let totalWithNeeds = 0;

  children.forEach((child) => {
    if (child.specialNeeds && child.specialNeeds.length > 0) {
      totalWithNeeds++;
      child.specialNeeds.forEach((need) => {
        counts[need] = (counts[need] || 0) + 1;
      });
    }
  });

  const result: DemographicCount[] = Object.entries(SPECIAL_NEEDS_LABELS).map(([key, label]) => ({
    key,
    label,
    count: counts[key] || 0,
    percentage: totalWithNeeds > 0 ? Math.round(((counts[key] || 0) / totalWithNeeds) * 100) : 0,
  }));

  return result.sort((a, b) => b.count - a.count);
};

const countMultiSelectField = <T extends string>(
  items: { [key: string]: T[] | undefined }[],
  field: string,
  labels: Record<T, string>
): DemographicCount[] => {
  const counts: Record<string, number> = {};
  let totalWithValues = 0;

  items.forEach((item) => {
    const values = item[field] as T[] | undefined;
    if (values && values.length > 0) {
      totalWithValues++;
      values.forEach((val) => {
        counts[val] = (counts[val] || 0) + 1;
      });
    }
  });

  const result: DemographicCount[] = (Object.entries(labels) as [string, string][]).map(([key, label]) => ({
    key,
    label,
    count: counts[key] || 0,
    percentage: totalWithValues > 0 ? Math.round(((counts[key] || 0) / totalWithValues) * 100) : 0,
  }));

  return result.sort((a, b) => b.count - a.count);
};

const countAgeGroups = (children: Child[]): DemographicCount[] => {
  const counts: Record<AgeGroup, number> = {
    "0-2": 0,
    "3-5": 0,
    "6-8": 0,
    "9-11": 0,
    "12-14": 0,
    "15-17": 0,
    "18+": 0,
  };
  let notSetCount = 0;
  const total = children.length;

  children.forEach((child) => {
    if (child.dob) {
      try {
        const ageGroup = getAgeGroupFromDob(child.dob.toDate());
        counts[ageGroup]++;
      } catch {
        notSetCount++;
      }
    } else {
      notSetCount++;
    }
  });

  const result: DemographicCount[] = Object.entries(AGE_GROUP_LABELS).map(([key, label]) => ({
    key,
    label,
    count: counts[key as AgeGroup],
    percentage: total > 0 ? Math.round((counts[key as AgeGroup] / total) * 100) : 0,
  }));

  if (notSetCount > 0) {
    result.push({
      key: "_not_set",
      label: "Not Set",
      count: notSetCount,
      percentage: total > 0 ? Math.round((notSetCount / total) * 100) : 0,
    });
  }

  return result;
};

const aggregateNoShowStats = (
  cases: { noShowStats?: NoShowStats }[]
): { noShowsByParty: DemographicCount[]; totalNoShows: number } => {
  const aggregated: NoShowStats = {
    total: 0,
    visitingParent: 0,
    custodialParent: 0,
    bothParents: 0,
    child: 0,
    supervisor: 0,
    other: 0,
  };

  cases.forEach((c) => {
    if (c.noShowStats) {
      aggregated.total += c.noShowStats.total || 0;
      aggregated.visitingParent += c.noShowStats.visitingParent || 0;
      aggregated.custodialParent += c.noShowStats.custodialParent || 0;
      aggregated.bothParents += c.noShowStats.bothParents || 0;
      aggregated.child += c.noShowStats.child || 0;
      aggregated.supervisor += c.noShowStats.supervisor || 0;
      aggregated.other += c.noShowStats.other || 0;
    }
  });

  const noShowsByParty: DemographicCount[] = [
    {
      key: "visiting_parent",
      label: NO_SHOW_PARTY_LABELS.visiting_parent,
      count: aggregated.visitingParent,
      percentage: aggregated.total > 0 ? Math.round((aggregated.visitingParent / aggregated.total) * 100) : 0,
    },
    {
      key: "custodial_parent",
      label: NO_SHOW_PARTY_LABELS.custodial_parent,
      count: aggregated.custodialParent,
      percentage: aggregated.total > 0 ? Math.round((aggregated.custodialParent / aggregated.total) * 100) : 0,
    },
    {
      key: "both_parents",
      label: NO_SHOW_PARTY_LABELS.both_parents,
      count: aggregated.bothParents,
      percentage: aggregated.total > 0 ? Math.round((aggregated.bothParents / aggregated.total) * 100) : 0,
    },
    {
      key: "child",
      label: NO_SHOW_PARTY_LABELS.child,
      count: aggregated.child,
      percentage: aggregated.total > 0 ? Math.round((aggregated.child / aggregated.total) * 100) : 0,
    },
    {
      key: "supervisor",
      label: NO_SHOW_PARTY_LABELS.supervisor,
      count: aggregated.supervisor,
      percentage: aggregated.total > 0 ? Math.round((aggregated.supervisor / aggregated.total) * 100) : 0,
    },
    {
      key: "other",
      label: NO_SHOW_PARTY_LABELS.other,
      count: aggregated.other,
      percentage: aggregated.total > 0 ? Math.round((aggregated.other / aggregated.total) * 100) : 0,
    },
  ].sort((a, b) => b.count - a.count);

  return { noShowsByParty, totalNoShows: aggregated.total };
};

interface UseDemographicsDataOptions {
  programTypeFilter?: ProgramType | "all";
  yearFilter?: number | "all";
}

/**
 * Get available years from cases based on their intake date or creation date.
 * Returns years in descending order (most recent first).
 */
export const getAvailableYears = (
  cases: { intakeDate?: { toDate: () => Date }; createdAt?: { toDate: () => Date } }[]
): number[] => {
  const years = new Set<number>();

  cases.forEach((c) => {
    // Prefer intakeDate, fall back to createdAt
    const date = c.intakeDate?.toDate() || c.createdAt?.toDate();
    if (date) {
      years.add(date.getFullYear());
    }
  });

  return Array.from(years).sort((a, b) => b - a);
};

/**
 * Helper to count boolean fields as Yes/No demographics
 */
const countBooleanField = (
  items: { [key: string]: boolean | undefined }[],
  field: string
): DemographicCount[] => {
  let yesCount = 0;
  let noCount = 0;
  let notSetCount = 0;
  const total = items.length;

  items.forEach((item) => {
    const value = item[field];
    if (value === true) {
      yesCount++;
    } else if (value === false) {
      noCount++;
    } else {
      notSetCount++;
    }
  });

  const result: DemographicCount[] = [
    {
      key: "yes",
      label: "Yes",
      count: yesCount,
      percentage: total > 0 ? Math.round((yesCount / total) * 100) : 0,
    },
    {
      key: "no",
      label: "No",
      count: noCount,
      percentage: total > 0 ? Math.round((noCount / total) * 100) : 0,
    },
  ];

  if (notSetCount > 0) {
    result.push({
      key: "_not_set",
      label: "Not Set",
      count: notSetCount,
      percentage: total > 0 ? Math.round((notSetCount / total) * 100) : 0,
    });
  }

  return result.sort((a, b) => b.count - a.count);
};

export const useDemographicsData = (
  options: UseDemographicsDataOptions = {}
): DemographicsData & { loading: boolean; availableYears: number[] } => {
  const { programTypeFilter = "all", yearFilter = "all" } = options;
  const { childList, loading: childLoading } = useChild();
  const { cases, loading: caseLoading } = useCase();
  const { parents, loading: parentLoading } = useParent();

  // Calculate available years from all cases (before filtering)
  const availableYears = useMemo(() => getAvailableYears(cases), [cases]);

  const demographicsData = useMemo(() => {
    // Filter cases by program type if specified
    let filteredCases =
      programTypeFilter === "all"
        ? cases
        : cases.filter(
            (c) =>
              c.programTypes?.includes(programTypeFilter) ||
              c.programType === programTypeFilter
          );

    // Filter cases by year if specified
    if (yearFilter !== "all") {
      filteredCases = filteredCases.filter((c) => {
        const date = c.intakeDate?.toDate() || c.createdAt?.toDate();
        return date && date.getFullYear() === yearFilter;
      });
    }

    // Get case IDs for filtered cases to filter children and parents
    const filteredCaseIds = new Set(filteredCases.map((c) => c.id));

    // Filter children to only include those belonging to filtered cases
    const filteredChildren = childList.filter((child) =>
      filteredCaseIds.has(child.caseId)
    );

    // Filter parents to only include those belonging to filtered cases
    const filteredParents = parents.filter((parent) =>
      filteredCaseIds.has(parent.caseId)
    );

    // Calculate coverage stats
    const childrenWithAnyDemographics = filteredChildren.filter(
      (c) =>
        c.race ||
        c.sex ||
        c.primaryLanguage ||
        c.dob ||
        (c.specialNeeds && c.specialNeeds.length > 0)
    ).length;

    const parentsWithAnyDemographics = filteredParents.filter(
      (p) =>
        p.race ||
        p.sex ||
        p.primaryLanguage ||
        p.maritalStatus ||
        p.incomeLevel ||
        p.educationLevel ||
        p.employmentStatus
    ).length;

    const casesWithReason = filteredCases.filter(
      (c) => c.reasonForSupervision
    ).length;

    // Aggregate no-show statistics across all filtered cases
    const { noShowsByParty, totalNoShows } = aggregateNoShowStats(filteredCases);

    return {
      // Totals
      totalChildren: filteredChildren.length,
      totalParents: filteredParents.length,
      totalCases: filteredCases.length,

      // Child demographics
      childRace: countByField(
        filteredChildren as unknown as { [key: string]: Race | undefined }[],
        "race",
        RACE_LABELS
      ),
      childSex: countByField(
        filteredChildren as unknown as { [key: string]: Sex | undefined }[],
        "sex",
        SEX_LABELS
      ),
      childLanguage: countByField(
        filteredChildren as unknown as {
          [key: string]: PrimaryLanguage | undefined;
        }[],
        "primaryLanguage",
        LANGUAGE_LABELS
      ),
      childAgeGroups: countAgeGroups(filteredChildren),
      specialNeeds: countSpecialNeeds(filteredChildren),

      // Parent demographics
      parentRace: countByField(
        filteredParents as unknown as { [key: string]: Race | undefined }[],
        "race",
        RACE_LABELS
      ),
      parentSex: countByField(
        filteredParents as unknown as { [key: string]: Sex | undefined }[],
        "sex",
        SEX_LABELS
      ),
      parentLanguage: countByField(
        filteredParents as unknown as {
          [key: string]: PrimaryLanguage | undefined;
        }[],
        "primaryLanguage",
        LANGUAGE_LABELS
      ),
      parentMaritalStatus: countByField(
        filteredParents as unknown as {
          [key: string]: MaritalStatus | undefined;
        }[],
        "maritalStatus",
        MARITAL_STATUS_LABELS
      ),
      parentIncomeLevel: countByField(
        filteredParents as unknown as {
          [key: string]: IncomeLevel | undefined;
        }[],
        "incomeLevel",
        INCOME_LEVEL_LABELS
      ),
      parentEducationLevel: countByField(
        filteredParents as unknown as {
          [key: string]: EducationLevel | undefined;
        }[],
        "educationLevel",
        EDUCATION_LEVEL_LABELS
      ),
      parentEmploymentStatus: countByField(
        filteredParents as unknown as {
          [key: string]: EmploymentStatus | undefined;
        }[],
        "employmentStatus",
        EMPLOYMENT_STATUS_LABELS
      ),
      parentHousingStatus: countByField(
        filteredParents as unknown as {
          [key: string]: HousingStatus | undefined;
        }[],
        "housingStatus",
        HOUSING_STATUS_LABELS
      ),
      parentRuralArea: countBooleanField(
        filteredParents as unknown as { [key: string]: boolean | undefined }[],
        "isRuralArea"
      ),
      parentTranslatorNeeded: countBooleanField(
        filteredParents as unknown as { [key: string]: boolean | undefined }[],
        "translatorNeeded"
      ),

      // Case demographics
      reasonForSupervision: countByField(
        filteredCases as unknown as {
          [key: string]: ReasonForSupervision | undefined;
        }[],
        "reasonForSupervision",
        REASON_LABELS
      ),
      presentingConcerns: countMultiSelectField(
        filteredCases as unknown as {
          [key: string]: PresentingConcern[] | undefined;
        }[],
        "presentingConcerns",
        PRESENTING_CONCERN_LABELS
      ),
      noShowsByParty,
      totalNoShows,

      // Coverage stats
      childrenWithDemographics: childrenWithAnyDemographics,
      parentsWithDemographics: parentsWithAnyDemographics,
      casesWithDemographics: casesWithReason,
    };
  }, [childList, parents, cases, programTypeFilter, yearFilter]);

  return {
    ...demographicsData,
    loading: Boolean(childLoading.list || caseLoading.list || parentLoading.list),
    availableYears,
  };
};
